// ═══════════════════════════════════════════════════════════════════════════════
//  src/pages/DojoPage.jsx
//  EL DOJO — ruta /dojo
//
//  Cuatro formatos de alta intensidad. Ninguno pasa del minuto y medio, ninguno
//  pide elegir nivel, ninguno empieza con una explicación.
//
//  ── LA REGLA DE ESTA PANTALLA: CERO FRICCIÓN ────────────────────────────────
//  Del hub al primer estímulo hay UN toque. Sin configurar, sin cargar lección,
//  sin elegir dificultad. Esa es la diferencia entre un formato que se juega en
//  la fila del banco y uno que se pospone para "cuando tenga tiempo".
//
//  ── LENGUAJE VISUAL ─────────────────────────────────────────────────────────
//  Adopta el estándar de FLUENCY NEXUS: abismo índigo, fondo vivo de
//  partículas, tarjetas gobernadas por una sola variable de color (`--dj-mc`)
//  y profundidad por resplandor. Al entrar a un formato, su color toma la
//  pantalla entera — botones, reloj, marcador y resultado — para que cada
//  estación se sienta un lugar distinto y no una pestaña del mismo sitio.
//
//  AISLAMIENTO: módulo autónomo con CSS prefijado `.dj-`. Reutiliza el Header
//  de la app autenticada y `browserSpeech`; no toca ningún componente existente.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import usePageTracker from '../hooks/usePageTracker.js';

import SparringReflejos from '../dojo/SparringReflejos';
import SwipeSupervivencia from '../dojo/SwipeSupervivencia';
import ShadowingCiego from '../dojo/ShadowingCiego';
import CrisisRoom from '../dojo/CrisisRoom';
import DojoAmbient from '../dojo/DojoAmbient';

import { CONTEXTS } from '../dojo/dojoBank';
import { unlockAudio } from '../audio/fx';
import { getRecord } from '../dojo/dojoFx';
import '../dojo/dojo.css';

// El color de cada formato ya no es un hexadecimal fijo de modo oscuro: es un
// token que se resuelve por tema. Y ninguno es rojo ni verde — en NSL esos dos
// significan error y acierto, y el Crisis Room en rojo hacia que su tarjeta se
// leyera como un fallo antes de tocarla.
const FORMATS = [
  {
    id: 'sparring',
    icon: '⚡',
    phase: 'Formato 01 · Reacción',
    name: 'Sparring de reflejos',
    text: 'Spacy te lanza una frase y dos respuestas. 4 segundos para elegir la del nativo.',
    tags: ['~90 s', 'Sin voz', '2 opciones'],
    accent: 'var(--dj-f-sparring)',
    Component: SparringReflejos,
  },
  {
    id: 'swipe',
    icon: '🃏',
    phase: 'Formato 02 · Patrón',
    name: 'Swipe de supervivencia',
    text: 'Derecha si suena bien, izquierda si está mal. Y cada vez más rápido.',
    tags: ['~60 s', 'Sin voz', 'Combo ×2'],
    accent: 'var(--dj-f-swipe)',
    Component: SwipeSupervivencia,
  },
  {
    id: 'shadowing',
    icon: '🎧',
    phase: 'Formato 03 · Prosodia',
    name: 'Shadowing a ciegas',
    text: 'Escuchas a velocidad real con el texto borroso. Hablas encima, al mismo ritmo.',
    tags: ['~80 s', 'Micrófono', 'Ritmo'],
    accent: 'var(--dj-f-shadowing)',
    Component: ShadowingCiego,
  },
  {
    id: 'crisis',
    icon: '🚨',
    phase: 'Formato 04 · Presión',
    name: 'Crisis Room',
    text: 'Una situación límite. 45 segundos. Y un jefe que evalúa si lograste la misión.',
    tags: ['~45 s', 'Micrófono', 'Veredicto IA'],
    accent: 'var(--dj-f-crisis)',
    Component: CrisisRoom,
  },
];

const CTX_KEY = 'nsl_dojo_ctx';

export default function DojoPage() {
  usePageTracker('dojo', 'El Dojo', null, 'studying');

  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [ctx, setCtx] = useState(() => {
    try { return localStorage.getItem(CTX_KEY) || 'general'; } catch { return 'general'; }
  });

  useEffect(() => {
    try { localStorage.setItem(CTX_KEY, ctx); } catch { /* modo privado */ }
  }, [ctx]);

  /* El AudioContext debe nacer de un gesto real del usuario, o el navegador
     silencia toda la sesión sin decir nada. */
  const launch = (id) => {
    unlockAudio();
    setActive(id);
  };

  const current = FORMATS.find((f) => f.id === active);
  const records = FORMATS.reduce((n, f) => n + (getRecord(f.id) > 0 ? 1 : 0), 0);

  return (
    <>
      <Header />
      <main className="dj-root">
        <DojoAmbient />

        <div
          className="dj-shell dj-screen"
          key={active || 'hub'}
          style={current ? { '--dj-mc': current.accent } : undefined}
        >
          {current ? (
            <current.Component ctx={ctx} accent={current.accent} onExit={() => setActive(null)} />
          ) : (
            <>
              <header className="dj-hero">
                <div className="dj-hero-tag">⚔️ Alta intensidad · 4 formatos</div>
                <h1 className="dj-h1">
                  <span className="dj-ac">EL</span>
                  <span className="dj-glow">DOJO</span>
                </h1>
                <p className="dj-lead">
                  El inglés entrenado como habilidad refleja, no como tema de
                  estudio. Retos de menos de minuto y medio que empiezan con un
                  toque.
                </p>
                <div className="dj-pills">
                  <span className="dj-pill">Sin configurar</span>
                  <span className="dj-pill">Sin elegir nivel</span>
                  {records > 0
                    ? <span className="dj-pill"><b>{records}</b> marcas activas</span>
                    : <span className="dj-pill">Tu primera marca te espera</span>}
                </div>
              </header>

              <div className="dj-ctxbar" role="group" aria-label="Contexto del entrenamiento">
                {CONTEXTS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="dj-ctx"
                    aria-pressed={ctx === c.id}
                    onClick={() => setCtx(c.id)}
                  >
                    {c.icon} {c.label}
                  </button>
                ))}
              </div>

              <div className="dj-grid">
                {FORMATS.map((f) => {
                  const rec = getRecord(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className="dj-card"
                      style={{ '--dj-mc': f.accent }}
                      onClick={() => launch(f.id)}
                    >
                      <span className="dj-card-glow" aria-hidden="true" />
                      <span className="dj-card-icon" aria-hidden="true">{f.icon}</span>
                      <span className="dj-card-phase">{f.phase}</span>
                      <span className="dj-card-name">{f.name}</span>
                      <span className="dj-card-desc">{f.text}</span>
                      <div className="dj-card-tags">
                        {f.tags.map((t) => <span key={t} className="dj-card-tag">{t}</span>)}
                        {rec > 0 && <span className="dj-card-tag">★ {rec}</span>}
                      </div>
                      <span className="dj-card-arrow" aria-hidden="true">→</span>
                      <span className="dj-card-bar" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>

              <p className="dj-caveat">
                Shadowing y Crisis Room usan tu micrófono y funcionan mejor en
                Chrome o Edge — Safari todavía no reconoce voz de forma fiable.
                Sparring y Swipe corren en cualquier navegador.
              </p>

              <div className="dj-actions">
                <button type="button" className="dj-btn dj-btn-ghost" onClick={() => navigate('/entrenar')}>
                  Volver al gimnasio
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}
